import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, Input, FormControl, FormLabel, useToast,
  Box, Badge, Flex, Textarea, Text,
} from '@chakra-ui/react';
import api from '../api';

export default function ContactModal({ isOpen, onClose, companyId, onCreated }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', mobile: '', landline: '', email: '', job_title: '', notes: '',
  });
  const [selectedCompanyId, setSelectedCompanyId] = useState(companyId || null);
  const [companySearch, setCompanySearch] = useState('');
  const [companyResults, setCompanyResults] = useState([]);
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      setForm({ first_name: '', last_name: '', mobile: '', landline: '', email: '', job_title: '', notes: '' });
      setSelectedCompanyId(companyId || null);
      setCompanySearch('');
      setCompanyResults([]);
      setSelectedCompanyName('');
      if (companyId) {
        api.get(`/companies/search?q=&limit=1`).catch(() => {});
      }
    }
  }, [isOpen, companyId]);

  const searchCompanies = async (q) => {
    if (q.length < 1) { setCompanyResults([]); return; }
    try { const { data } = await api.get(`/companies/search?q=${encodeURIComponent(q)}`); setCompanyResults(data); } catch(e) {}
  };

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    if (!form.first_name.trim()) {
      toast({ title: 'First name is required', status: 'warning', duration: 2000 });
      return;
    }
    if (!selectedCompanyId) {
      toast({ title: 'Please select a company first', status: 'warning', duration: 2000 });
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/contacts', { ...form, company_id: selectedCompanyId });
      toast({ title: 'Contact created', status: 'success', duration: 2000 });
      onCreated(data);
    } catch (err) {
      toast({ title: 'Failed to create contact', status: 'error', duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">New Contact</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          <VStack spacing={3}>
            {!companyId && (
              <FormControl>
                <FormLabel fontSize="xs" mb={1}>Company *</FormLabel>
                {selectedCompanyId && selectedCompanyName ? (
                  <Flex align="center" gap={2}>
                    <Badge colorScheme="blue" fontSize="11px">{selectedCompanyName}</Badge>
                    <Button size="xs" variant="ghost" onClick={() => { setSelectedCompanyId(null); setSelectedCompanyName(''); setCompanySearch(''); }}>Change</Button>
                  </Flex>
                ) : (
                  <>
                    <Input size="sm" placeholder="Search company..." value={companySearch}
                      onChange={e => { setCompanySearch(e.target.value); searchCompanies(e.target.value); }} />
                    {companyResults.length > 0 && (
                      <Box border="1px" borderColor="gray.200" borderRadius="md" mt={1} maxH="120px" overflowY="auto">
                        {companyResults.map(c => (
                          <Box key={c.id} px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
                            onClick={() => { setSelectedCompanyId(c.id); setSelectedCompanyName(c.name); setCompanyResults([]); setCompanySearch(c.name); }}>
                            {c.name}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </FormControl>
            )}
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>First Name *</FormLabel>
              <Input size="sm" value={form.first_name} onChange={handleChange('first_name')} autoFocus />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Last Name</FormLabel>
              <Input size="sm" value={form.last_name} onChange={handleChange('last_name')} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Mobile</FormLabel>
              <Input size="sm" value={form.mobile} onChange={handleChange('mobile')} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Landline</FormLabel>
              <Input size="sm" value={form.landline} onChange={handleChange('landline')} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Email</FormLabel>
              <Input size="sm" type="email" value={form.email} onChange={handleChange('email')} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Job Title</FormLabel>
              <Input size="sm" value={form.job_title} onChange={handleChange('job_title')} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Notes <Text as="span" color="gray.400" fontWeight="400">(relationship, context, etc.)</Text></FormLabel>
              <Textarea size="sm" value={form.notes} onChange={handleChange('notes')} rows={3} placeholder="Summary of our relationship, useful context…" />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button size="sm" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
            onClick={handleSave} isLoading={saving}>
            Add Contact
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
